import {
  CategoryScale,
  Chart,
  ChartData,
  ChartDataset,
  ChartOptions,
  ChartType,
  Interaction,
  InteractionModeFunction,
  LinearScale,
  LineController,
  LineElement,
  Plugin,
  PointElement,
  Tooltip,
  TooltipPositionerFunction,
} from "chart.js";

import { useEffect, useRef } from "react";

import { alpha } from "@mui/material";

Chart.register(
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip
);

/** A score per check-in; null when the check-in was not completed. */
export type ChartPoints = (number | null)[];

export const realPoints = (points: ChartPoints) =>
  points.filter((point): point is number => point !== null);

export const hasTrend = (points: ChartPoints) => realPoints(points).length >= 2;

/**
 * Missing check-ins are bridged with a lighter, dashed line (spanGaps) so the
 * trend stays readable without implying a measured score.
 */
export const gapSegment = (
  color: string
): ChartDataset<"line", ChartPoints>["segment"] => ({
  borderDash: (ctx) => (ctx.p0.skip || ctx.p1.skip ? [6, 6] : undefined),
  borderColor: (ctx) =>
    ctx.p0.skip || ctx.p1.skip ? alpha(color, 0.5) : undefined,
});

export const lineDefaults: Partial<ChartDataset<"line", ChartPoints>> = {
  borderCapStyle: "round",
  borderJoinStyle: "round",
  tension: 0.4,
  pointRadius: 0,
  pointHitRadius: 12,
  fill: false,
  spanGaps: true,
};

const lineGlow: Plugin<"line"> = {
  id: "lineGlow",
  beforeDatasetDraw: (chart, args) => {
    const { borderColor } = chart.data.datasets[args.index];

    chart.ctx.save();

    if (typeof borderColor === "string") {
      chart.ctx.shadowColor = alpha(borderColor, 0.3);
      chart.ctx.shadowBlur = 8;
      chart.ctx.shadowOffsetY = 4;
    }
  },
  afterDatasetDraw: (chart) => {
    chart.ctx.restore();
  },
};

export const lineGlowPlugins: Plugin<"line">[] = [lineGlow];

const missingMarkerSize = 3;

/**
 * Where to draw the X for a null slot. Between two scores it sits on the
 * dashed bridge; before the first or after the last score (no line there) it
 * sits level with the nearest score; with no scores it is vertically centered.
 */
const missingMarkerY = (
  chart: Chart,
  datasetIndex: number,
  index: number,
  x: number
) => {
  const meta = chart.getDatasetMeta(datasetIndex);
  const values = chart.data.datasets[datasetIndex].data;

  const line = meta.dataset as LineElement | undefined;
  const onLine = line?.interpolate({ x, y: 0 }, "x");
  const bridge = Array.isArray(onLine) ? onLine[0] : onLine;
  if (bridge) {
    return bridge.y;
  }

  for (let offset = 1; offset < values.length; ++offset) {
    for (const neighbor of [index - offset, index + offset]) {
      if (values[neighbor] != null) {
        return meta.data[neighbor].y;
      }
    }
  }

  return (chart.chartArea.top + chart.chartArea.bottom) / 2;
};

/**
 * Draws an X in each null slot so a missed check-in reads as "no score"
 * rather than a measured value.
 */
export const missingScoreMarkers: Plugin<"line"> = {
  id: "missingScoreMarkers",
  afterDatasetsDraw: (chart) => {
    const { ctx, scales } = chart;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!chart.isDatasetVisible(datasetIndex)) {
        return;
      }

      ctx.save();
      ctx.strokeStyle =
        typeof dataset.borderColor === "string"
          ? alpha(dataset.borderColor, 0.7)
          : "#989A9C";
      ctx.lineWidth = 1.5;
      ctx.lineCap = "round";

      dataset.data.forEach((value, index) => {
        if (value !== null) {
          return;
        }

        const x = scales.x.getPixelForValue(index);
        const y = missingMarkerY(chart, datasetIndex, index, x);

        ctx.beginPath();
        ctx.moveTo(x - missingMarkerSize, y - missingMarkerSize);
        ctx.lineTo(x + missingMarkerSize, y + missingMarkerSize);
        ctx.moveTo(x + missingMarkerSize, y - missingMarkerSize);
        ctx.lineTo(x - missingMarkerSize, y + missingMarkerSize);
        ctx.stroke();
      });

      ctx.restore();
    });
  },
};

declare module "chart.js" {
  interface InteractionModeMap {
    checkInSlot: InteractionModeFunction;
  }
  interface TooltipPositionerMap {
    checkInSlot: TooltipPositionerFunction<ChartType>;
  }
}

/**
 * Like the built-in "index" mode, but also returns null slots. The built-in
 * modes skip points without a y pixel, so a missed check-in gets no tooltip.
 */
Interaction.modes.checkInSlot = (chart, e) => {
  const { x } = e;
  const { left, right } = chart.chartArea;
  const count = chart.data.labels?.length ?? 0;
  if (x === null || x < left || x > right || count === 0) {
    return [];
  }

  const value = chart.scales.x.getValueForPixel(x) ?? 0;
  const index = Math.min(Math.max(Math.round(value), 0), count - 1);

  return chart.data.datasets.flatMap((_, datasetIndex) => {
    const element = chart.getDatasetMeta(datasetIndex).data[index];
    return chart.isDatasetVisible(datasetIndex) && element
      ? [{ element, datasetIndex, index }]
      : [];
  });
};

/**
 * Anchors the tooltip at the hovered slot; null slots use the same y as their
 * X marker since their point has no y pixel.
 */
Tooltip.positioners.checkInSlot = function (items) {
  if (items.length === 0) {
    return false;
  }

  const x = items[0].element.x;
  const ys = items.map((item) =>
    Number.isFinite(item.element.y)
      ? item.element.y
      : missingMarkerY(this.chart as Chart, item.datasetIndex, item.index, x)
  );

  return { x, y: ys.reduce((sum, y) => sum + y, 0) / ys.length };
};

export const scoreMin = 0.75;

export const scoreMax = 5.4;

export const scoreBands: {
  value: number;
  label: string;
}[] = [
  {
    value: 1.25,
    label: "Low",
  },
  {
    value: 2.5,
    label: "Moderate",
  },
  {
    value: 3.75,
    label: "Good",
  },
  {
    value: 5,
    label: "Excellent",
  },
];

/**
 * Renders a chart.js line chart into a canvas. The chart is created once and
 * updated in place on every render, then destroyed on unmount.
 */
export function useLineChart(
  data: ChartData<"line", ChartPoints, string>,
  options: ChartOptions<"line">,
  plugins: Plugin<"line">[] = []
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart<"line", ChartPoints, string> | null>(null);

  useEffect(() => {
    if (!canvasRef.current) {
      // The canvas was unmounted (e.g. an empty state replaced it).
      chartRef.current?.destroy();
      chartRef.current = null;
      return;
    }

    if (!chartRef.current) {
      chartRef.current = new Chart(canvasRef.current, {
        type: "line",
        data,
        options,
        plugins,
      });
      return;
    }

    chartRef.current.data = data;
    chartRef.current.options = options;
    chartRef.current.update();
  });

  useEffect(
    () => () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    },
    []
  );

  return canvasRef;
}
